import {
  BuildContext,
  ExportColumn,
  ExportOptions,
  ExportPayloadSection,
} from '../../types';
import { CSV } from '../../constants';
import escapeCsvField from './escapeCsvField';
import escapeDelimitedValue from './escapeDelimitedValue';
import { isNumberExportColumn } from '../isNumberExportColumn';
import reportProgressAndMaybeYield from '../reportProgressAndMaybeYield';
import resolveTextCellValue from '../resolveTextCellValue';

const buildDelimitedContent = async (
  sections: ExportPayloadSection[],
  options: ExportOptions,
  context: BuildContext
): Promise<string> => {
  const isCsvFormat = options.format === CSV;

  // Also used for header/footer: TXT never gets CSV's formula guard, only the delimiter/quote/CRLF quoting.
  const escapeName = (value: string): string =>
    isCsvFormat
      ? escapeCsvField(value, options.delimiter)
      : escapeDelimitedValue(value, options.delimiter);

  // Only CSV reads the column type, to decide whether the formula guard applies.
  const escapeCell = (value: string, column: ExportColumn): string =>
    isCsvFormat
      ? escapeCsvField(value, options.delimiter, isNumberExportColumn(column))
      : escapeDelimitedValue(value, options.delimiter);

  const lines: string[] = [];
  // Single-section exports match legacy CSV/TXT samples without a synthetic section row.
  const showSectionLabels = sections.length > 1;

  if (options.header) {
    lines.push(escapeName(options.header));
  }

  for (
    let sectionIndex = 0;
    sectionIndex < sections.length;
    sectionIndex += 1
  ) {
    const section = sections[sectionIndex];

    if (showSectionLabels) {
      lines.push(
        isCsvFormat
          ? escapeCsvField(section.label, options.delimiter)
          : escapeDelimitedValue(`[${section.label}]`, options.delimiter)
      );
    }

    if (options.includeColumnNames) {
      lines.push(
        section.columns
          .map((column) => escapeName(column.label))
          .join(options.delimiter)
      );
    }

    for (const row of section.rows) {
      lines.push(
        section.columns
          .map((column) =>
            escapeCell(resolveTextCellValue(row, column, options), column)
          )
          .join(options.delimiter)
      );
      await reportProgressAndMaybeYield(context);
    }

    if (showSectionLabels || sectionIndex < sections.length - 1) {
      lines.push('');
    }
  }

  if (options.footer) {
    lines.push(escapeName(options.footer));
  }

  return lines.join('\r\n');
};

export default buildDelimitedContent;
